/** @file Checks an impostor's facet entries: table, sheet, variants, drawn angles, slots (17.1, R1). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { resolveSectorTable } from '@zilo/pose3d-format/tables/resolveSectorTable';
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { FacetEntry, FacetTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { throwError } from '@zilo/pose3d-std/errors/throwError';

function checkAngles(angles: number[] | undefined, path: string, tiles: number, table: string): void {
  if (angles !== undefined && angles.length !== tiles) {
    throwError(ERROR_CODES.DRAWN_ANGLES_LENGTH, { expected: tiles, got: angles.length, table }, { path, hint: 'one angle per drawn tile' });
  }
}

function checkVariants(entry: FacetEntry, path: string, puppet: Puppet): void {
  entry.variants.forEach((name, i) => {
    if (!puppet.variants.includes(name)) throwError(ERROR_CODES.VARIANT_UNKNOWN, { variant: name }, { path: `${path}/variants/${i}` });
  });
}

function checkSlots(entry: FacetEntry, path: string, tiles: number): void {
  for (const key of Object.keys(entry.slots)) {
    if (Number(key) >= tiles) failField(`${path}/slots/${key}`, `a tile index below ${tiles}`, key);
  }
}

function checkEntry(entry: FacetEntry, path: string, puppet: Puppet): void {
  const table = resolveSectorTable(entry.yaw, puppet);
  if (table === undefined) throwError(ERROR_CODES.SECTOR_TABLE_UNKNOWN, { table: entry.yaw }, { path: `${path}/yaw` });
  if (!Object.hasOwn(puppet.sheets, entry.sheet)) throwError(ERROR_CODES.SHEET_UNKNOWN, { sheet: entry.sheet }, { path: `${path}/sheet` });
  const tiles = tileCountOf(table);
  checkAngles(entry.drawnAzimuths, `${path}/drawnAzimuths`, tiles, entry.yaw);
  if ((entry.rotate === 'screen' || entry.rotate === 'parent') && entry.drawnScreenAngles === undefined) {
    failField(`${path}/drawnScreenAngles`, 'required for rotate screen/parent (design 15.4)', undefined);
  }
  checkAngles(entry.drawnScreenAngles, `${path}/drawnScreenAngles`, tiles, entry.yaw);
  checkVariants(entry, path, puppet);
  checkSlots(entry, path, tiles);
}

/** Throws the first reference error among the entries of `facets` (paths `<path>/<index>/...`). */
export function validateFacetTable(facets: FacetTable, path: string, puppet: Puppet): void {
  facets.forEach((entry, i) => { checkEntry(entry, `${path}/${i}`, puppet); });
}
